import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import Page from '../../components/Page/Page';
import { useInfiniteQuery, useQuery } from 'react-query';
import {
  createDeliveriesReport,
  fetchDeliveryDetails,
  fetchPastDeliveries,
  getDeliveriesReport,
  StartParam
} from '../../api/deliveries';
import { fetchDeliveryTrackingDriverDetails } from '../../api/tracking';
import PastDeliveryCard from '../../components/PastDeliveryCard/PastDeliveryCard';
import { routes } from '../../utils/routerHelper';
import { useHistory, useParams } from 'react-router-dom';
import styles from './PastDeliveriesPage.module.less';
import Button from '../../components/Button/Button';
import LoadMoreButton from '../../components/Button/LoadMoreButton';
import DownloadModal from '../../components/DownloadModal/DownloadModal';
import { isMobile } from '../../utils/browser';
import { whenCSVDownloadIsFailed, whenOrderIsFailedToRecreate, whenOrderIsRecreated } from './messages';
import { connect } from 'react-redux';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import { DeliveryReportStatus } from '../../api/dtos/deliveries';
import {
  DELIVERY_REPORT_ID_KEY,
  FETCH_DELIVERY_DETAILS_KEY,
  FETCH_DELIVERY_TRACKING_KEY,
  FETCH_PAST_DELIVERIES_KEY,
  PAST_DELIVERY_CACHE_TIME
} from '../../utils/constants';
import { getItem, removeItem, setItem } from '../../utils/localStorage';
import Title from '../../components/Page/Title';
import TitleIcon from '../../assets/img/titleIcon.svg?react';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import {
  cnfCreateAgainClicked,
  completedDeliveriesPageOpen,
  deliveryDetailsClicked,
  downloadRecordsClicked
} from '../../utils/analytika/events';
import { ANALYTIKA_SCREENS } from '../../utils/analytika/enums';
import { DownloadOutlined } from '@ant-design/icons';

export const PAST_DELIVERIES_LIMIT = 20;

export const DOWNLOAD_STATUS_FETCH_INTERVAL = 2 * 1000;

const fetchPastDeliveriesData = ({ pageParam }: { pageParam?: StartParam }) => {
  return fetchPastDeliveries({ start: pageParam, limit: PAST_DELIVERIES_LIMIT });
};

const fetchDeliveryTrackingQuery = ({ queryKey }: { queryKey: [string, string] }) => {
  const [, deliveryId] = queryKey;

  return fetchDeliveryTrackingDriverDetails(deliveryId);
};

const fetchDeliveryDetailsQuery = ({ queryKey }: { queryKey: [string, string] }) => {
  const [, deliveryId] = queryKey;

  return fetchDeliveryDetails(deliveryId);
};

type PastDeliveriesPageProps = {
  recreateDelivery: (id: string) => Promise<any>;
};

const callCreateDeliveriesDeliveriesReport = ({ queryKey }: { queryKey: [string, [Date, Date]] }) => {
  const [, [startDate, endDate]] = queryKey;
  return createDeliveriesReport({ startDate, endDate });
};

const getDeliveriesReportById = ({ queryKey }: { queryKey: [string, string] }) => {
  const [, id] = queryKey;
  return getDeliveriesReport(id);
};

type UseDownloadReportProps = {
  downloadDates?: [Date, Date];
};

const useDownloadReport = (props: UseDownloadReportProps) => {
  const { downloadDates } = props;
  const deliveryReportIdFromLS = getItem(DELIVERY_REPORT_ID_KEY);
  const [shouldCheckReportStatus, setShouldCheckReportStatus] = useState(Boolean(deliveryReportIdFromLS));
  const {
    data: createDeliveriesReportData,
    refetch,
    isFetching: createDeliveriesReportLoading,
    error: createDeliveriesReportError
    //@ts-ignore
  } = useQuery(['createDeliveriesReport', downloadDates], callCreateDeliveriesDeliveriesReport, {
    enabled: downloadDates?.length === 2,
    retry: 0,
    cacheTime: 0
  });
  const {
    data: deliveriesReportData,
    isFetching: deliveriesReportLoading,
    error: deliveriesReportError
  } = useQuery(
    ['getDeliveriesReport', createDeliveriesReportData?.id || deliveryReportIdFromLS],
    //@ts-ignore
    getDeliveriesReportById,
    {
      enabled: Boolean(createDeliveriesReportData?.id || deliveryReportIdFromLS) && shouldCheckReportStatus,
      retry: 3,
      refetchInterval: DOWNLOAD_STATUS_FETCH_INTERVAL,
      cacheTime: 0
    }
  );

  useEffect(() => {
    if (createDeliveriesReportData) {
      setItem(DELIVERY_REPORT_ID_KEY, createDeliveriesReportData.id!);
    }
  }, [createDeliveriesReportData]);

  useEffect(() => {
    if (deliveriesReportData && deliveriesReportData.status !== DeliveryReportStatus.PROCESSING) {
      if (deliveriesReportData.status === DeliveryReportStatus.FAILED) {
        removeItem(DELIVERY_REPORT_ID_KEY);
      }
      setShouldCheckReportStatus(false);
    }
  }, [setShouldCheckReportStatus, deliveriesReportData]);

  useEffect(() => {
    if (deliveriesReportError) {
      setShouldCheckReportStatus(false);
      whenCSVDownloadIsFailed();
      removeItem(DELIVERY_REPORT_ID_KEY);
    }
  }, [deliveriesReportError]);
  useEffect(() => {
    if (createDeliveriesReportError) {
      whenCSVDownloadIsFailed();
      removeItem(DELIVERY_REPORT_ID_KEY);
    }
  }, [createDeliveriesReportError]);

  const recreateReport = useCallback(() => {
    if (downloadDates) {
      refetch().then(() => {
        setShouldCheckReportStatus(true);
      });
    }
  }, [refetch, setShouldCheckReportStatus, downloadDates]);
  return {
    downloadData: createDeliveriesReportData,
    shouldCheckReportStatus,
    setShouldCheckReportStatus,
    recreateReport,
    isDownloading: createDeliveriesReportLoading,
    downloadError: createDeliveriesReportError,
    deliveriesReportData,
    deliveriesReportLoading,
    deliveriesReportError
  };
};

export const PastDeliveriesPage = (props: PastDeliveriesPageProps) => {
  const { recreateDelivery } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const urlPrams = useParams<{ deliveryId?: string }>();
  const selectedDeliveryId = Number(urlPrams.deliveryId) || undefined;
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadDates, setDownloadDates] = useState<[Date, Date]>();
  const { setShouldCheckReportStatus, recreateReport, isDownloading, deliveriesReportData, deliveriesReportLoading } =
    useDownloadReport({
      downloadDates
    });

  const { fireEvent } = useAnalytika();

  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isSuccess: isCompletedDeliveriesSuccess
  } = useInfiniteQuery(FETCH_PAST_DELIVERIES_KEY, fetchPastDeliveriesData, {
    cacheTime: PAST_DELIVERY_CACHE_TIME,
    getNextPageParam: lastGroup => {
      const lastItem = lastGroup[lastGroup.length - 1];
      return lastGroup.length >= PAST_DELIVERIES_LIMIT
        ? {
            id: lastItem.bookingId,
            timestamp: lastItem.createdAt
          }
        : undefined;
    }
  });
  const deliveries = data?.pages.flat();

  useEffect(() => {
    isCompletedDeliveriesSuccess &&
      fireEvent(completedDeliveriesPageOpen({ completed_deliveries_list: deliveries || [] }));
  }, [isCompletedDeliveriesSuccess]);

  const bookingId = deliveries?.find(delivery => delivery.id === selectedDeliveryId)?.bookingId;
  const { data: driverDetailsData, isLoading: isTrackingLoading } = useQuery(
    [FETCH_DELIVERY_TRACKING_KEY, bookingId],
    //@ts-ignore
    fetchDeliveryTrackingQuery,
    {
      enabled: Boolean(bookingId)
    }
  );
  const { data: deliveryDetails, isLoading: isDeliveryDetailsLoading } = useQuery(
    [FETCH_DELIVERY_DETAILS_KEY, bookingId],
    //@ts-ignore
    fetchDeliveryDetailsQuery,
    {
      enabled: Boolean(bookingId)
    }
  );

  const handleExpandClick = useCallback(
    (deliveryId: string) => {
      if (!deliveryId) {
        history.push(routes.pastDeliveries);
      } else {
        const deliverySelected = deliveries?.find(delivery => delivery.id === +deliveryId);
        fireEvent(
          deliveryDetailsClicked(ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES, {
            delivery_details: deliverySelected
          })
        );
        history.push(routes.pastDeliveriesWithParam.replace(':deliveryId', deliveryId));
      }
    },
    [history, deliveries]
  );

  const handleDownloadClick = useCallback(() => {
    fireEvent(downloadRecordsClicked());
    setShowDownloadModal(true);
  }, [setShowDownloadModal]);

  const onRecreate = useCallback(
    (id: string) => {
      const deliveryToRecreate = deliveries?.find(delivery => delivery.bookingId === id);
      fireEvent(cnfCreateAgainClicked(ANALYTIKA_SCREENS.MARKETPLACE_COMPLETED_DELIVERIES, deliveryToRecreate));
      return recreateDelivery(id)
        .then(() => {
          whenOrderIsRecreated();
        })
        .catch(error => {
          whenOrderIsFailedToRecreate(getErrorFromResponse(error));
        });
    },
    [deliveries]
  );

  const loadMore = useCallback(() => fetchNextPage(), [fetchNextPage]);
  const onDownloadModalClose = useCallback(() => {
    setShowDownloadModal(false);
  }, [setShowDownloadModal]);

  const onDateChange = useCallback(
    (startDate: Date, endDate: Date) => {
      removeItem(DELIVERY_REPORT_ID_KEY);
      setDownloadDates([startDate, endDate]);
      setShouldCheckReportStatus(true);
      onDownloadModalClose();
    },
    [setDownloadDates, onDownloadModalClose, setShouldCheckReportStatus]
  );

  const enhancedDeliveries = deliveries?.map(delivery => {
    if (selectedDeliveryId === delivery.id) {
      // Fallback can be removed once the completed deliveries API doesn't contain location details
      return {
        ...delivery,
        pickupLocation: deliveryDetails?.pickupLocation || delivery?.pickupLocation,
        dropOffLocation: deliveryDetails?.dropOffLocation || delivery?.dropOffLocation,
        driverDetails: driverDetailsData
      };
    } else {
      return delivery;
    }
  });

  return (
    <Page>
      <div className={styles.pageTitle}>
        <Title text={t('pastDeliveriesPage.title')} Icon={TitleIcon} />

        {!isMobile() && (
          <div className={styles.titleButtonContainer}>
            {deliveriesReportData && deliveriesReportData.status === DeliveryReportStatus.READY && (
              <Button type="link" href={deliveriesReportData.url} download={'test122221.csv'}>
                {t('pastDeliveriesPage.downloadStatus.ready')}
              </Button>
            )}
            {deliveriesReportData && deliveriesReportData.status === DeliveryReportStatus.FAILED && (
              <>
                <Typography.Text>{t('pastDeliveriesPage.downloadStatus.failed')}</Typography.Text>
                <Button className={styles.tryAgainBtn} type="link" onClick={recreateReport}>
                  {t('general.tryAgain')}
                </Button>
              </>
            )}
            <Button
              data-testid="downloadCSVButton"
              icon={<DownloadOutlined />}
              onClick={handleDownloadClick}
              loading={
                isDownloading ||
                deliveriesReportData?.status === DeliveryReportStatus.PROCESSING ||
                deliveriesReportLoading
              }
            >
              {t('pastDeliveriesPage.download')}
            </Button>
          </div>
        )}
      </div>

      <Row gutter={[0, 16]} className={styles.cardsContainer}>
        {enhancedDeliveries?.map(item => (
          <Col span={24} key={item.id}>
            <PastDeliveryCard
              {...item}
              handleExpandClick={handleExpandClick}
              isExpanded={selectedDeliveryId === item.id}
              onRecreate={onRecreate}
              isDeliveryDetailsLoading={isDeliveryDetailsLoading}
              isTrackingLoading={isTrackingLoading}
            />
          </Col>
        ))}
        {hasNextPage && (
          <Col className={styles.loadMoreContainer}>
            <LoadMoreButton onClick={loadMore} loading={isFetching} />
          </Col>
        )}
      </Row>
      {showDownloadModal && (
        <DownloadModal onClose={onDownloadModalClose} visible={showDownloadModal} onSubmit={onDateChange} />
      )}
    </Page>
  );
};

const mapDispatch = ({ deliveries: { recreateDelivery } }: any) => ({
  recreateDelivery
});

export default connect(null, mapDispatch)(PastDeliveriesPage);
